import { Anchor, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { isApiError } from "../../../api/errors";
import { getGetAssetQueryOptions, useGetAsset } from "../../../api/generated/endpoints/assets/assets";
import {
  quoteJob,
  useGetJob,
  useStartJob,
} from "../../../api/generated/endpoints/generation/generation";
import type {
  Asset,
  Catalog,
  CatalogJobType,
  GenerationType,
  Job,
  SourceRef,
} from "../../../api/generated/model";
import { useIdempotencyKey } from "../../../platform/idempotency";
import { QUOTE_DEBOUNCE_MS } from "../../../platform/jobs/priced-start";
import { useGenerateImage } from "../../../platform/media/GenerateImageDialog";
import { pictureOf, type PickedImage } from "../../../platform/media/ImagePicker";
import { useImageUpload, type ImageUpload } from "../../../platform/media/image-upload";
import {
  cloneDraft,
  cloneModelNotice,
  type Kept,
  defaultModel,
  draftFor,
  isToolType,
  jobRequestOf,
  quoteRequestOf,
  modelsFor,
  shotsFor,
  offeredVideoTypes,
  typeLabel,
  typesFor,
  withFrames,
  type CatalogModel,
  type Medium,
} from "../draft";
import type { DraftShape, Frames, GenerateDraft } from "../draft-state";
import { ProductPicker, type ProductChoice } from "../../../platform/media/ProductPicker";
import { ACTION_TOOLS, actionOf, type ActionTool } from "../tools";
import { toolInputState, toolReferencesMax, toolVideosMax } from "../tools/inputs";
import { referenceCap, referencesLeft, trimImages } from "../image/references";
import { asTweak, isTweak, tweakRatio } from "../image/tweak";
import { audioLimitLines, imageLimitLines, videoLimitLines } from "../video/limits";
import { advancedCapsOf, advancedOf, type AdvancedDraft } from "../video/advanced";
import { elementCapsOf, sentElements, type ElementDraft } from "../video/elements";
import {
  inputState,
  middleFramesMax,
  referenceImagesMax,
  referenceMax,
  VIDEO_TYPES,
  type VideoInput,
} from "../video/modes";
import { chosen, footerOptions } from "../video/options";
import { multiShotOf, shortestOf } from "../video/shots";
import { ReferencesDialog } from "../../../platform/media/ReferencesDialog";
import { countOf, selectUpTo } from "../../../ui/format";
import type { SourceView } from "./source";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId";
/** Live's title of a frame's Generate dialog (the form header's `ImageGenerationModal`). */
const FRAME_GENERATE = "Generate frame";
/** A chosen image and the tool that chose it: the products' popover, or the library. */
type Picked = { via: "product"; choice: ProductChoice } | { via: "library"; image: PickedImage };

/** Where a chosen image goes: the source (a video's start frame), or a video's end frame. */
export type FrameSlot = "start" | "end";
/** What the products' popover fills: a frame, a video's references (#232) or its keyframes. */
export type ProductsTarget = FrameSlot | "references" | "keyframes";

/** The library dialog's title and line, in live's words for the tool that opened it. */
export interface PickingDialog {
  title: string;
  subtitle: string;
}

export interface ComposerProps {
  catalog: Catalog;
  /** The studio's page: only this medium's types are offered. */
  medium: Medium;
  /** The image to start from (a tweak or a video) when the page opens, or null for text. */
  initialSource: SourceRef | null;
  initialType: string | undefined;
  /**
   * The library video a link names for a tool on the composer (a video's Edit, S1c): the page
   * opens on that tool with the video as its source video.
   */
  initialVideo?: string | undefined;
  /** An earlier job to fill the draft from (`?clone=`), or undefined for the model's defaults. */
  clone: Job | undefined;
  /** Whether the clone takes a library file back (the Studio read them): not one gone since. */
  cloneKept?: Kept | undefined;
  /** How many of the clone's library files are gone since, which its draft leaves out. */
  cloneLeftOut?: number | undefined;
  /**
   * A library image the library's Clone names (`?advanced=`): the image composer opens in live's
   * Advanced mode with it as its reference (live's `onImageClone`).
   */
  initialAdvanced?: PickedImage | undefined;
  /** A library image the library's Layerize names (`?layerize=`): live's Layerize mode on it. */
  initialLayerize?: PickedImage | undefined;
  onStarted: (job: Job) => void;
  /** Told the draft's count and aspect ratio as they change, for the empty My Generations. */
  onShapeChange: (shape: DraftShape) => void;
}

/**
 * What both studios' composers share (design-system.md 3.3): the one draft, the price, the
 * start, and the image a change or a video starts from. The price comes by itself, from the
 * same body the start sends (G1: the quote and the start price one body), half a second after
 * the draft stops changing; the start sends that body with the price's maximum and an
 * Idempotency-Key. The image and video composers lay it out; neither keeps state of its own.
 */
export function useComposer(props: ComposerProps) {
  const { catalog, medium, initialSource, initialType, clone, onStarted, onShapeChange } = props;
  const { teamId, brandId } = useParams({ from: ROUTE });
  const initialTypes = typesFor(catalog, initialSource !== null, medium);
  const firstType = initialTypes.find((each) => each.id === initialType) ?? initialTypes[0]!;
  // A video's Edit from the library (S1c): the tool whose job the link names, on its rows; and a
  // clone of a tool's job (Video Edit, Video Extend) opens that tool again (issue 828, item 23).
  const linkedTool =
    clone !== undefined
      ? (ACTION_TOOLS.find((each) => each.job?.type === clone.type) ?? null)
      : props.initialVideo === undefined
        ? null
        : (ACTION_TOOLS.find((each) => each.job?.type === initialType) ?? null);
  const linkedToolType = catalog.job_types.find((each) => each.id === linkedTool?.job?.type);
  const linkedToolModels = linkedToolType === undefined ? [] : modelsFor(catalog, linkedToolType);
  // The type a clone's draft opens on, whose rows decide whether its model is still offered: a
  // composer tool's own (Video Edit), else the composer's first. Not the composer's first for a
  // tool: a Video Edit clone back on its Omni Flash Edit said "now on Kling v3 Pro" (issue 828,
  // item 23, dev d134d538).
  const clonedOn =
    clone !== undefined && linkedToolType !== undefined && linkedToolModels.length > 0
      ? linkedToolType
      : firstType;
  const [stored, setDraft] = useState<GenerateDraft>(() => {
    if (linkedToolType !== undefined && linkedToolModels.length > 0 && clone !== undefined) {
      // The tool's own type, its video and its prompt back (`cloneDraft` keeps the video).
      return cloneDraft(catalog, linkedToolType, clone, null, props.cloneKept);
    }
    if (linkedToolType !== undefined && linkedToolModels.length > 0 && props.initialVideo !== undefined) {
      const opened = draftFor(linkedToolType, defaultModel(linkedToolModels));
      return { ...opened, sourceVideo: { kind: "asset", asset_id: props.initialVideo } };
    }
    // A clone of a form tool's job (Motion Control) is the form's: the composer under it starts
    // empty, so it prices nothing of its own.
    if (clone === undefined || ACTION_TOOLS.some((each) => each.form === clone.type)) {
      return draftFor(firstType, defaultModel(modelsFor(catalog, firstType)), "", clone === undefined ? initialSource : null);
    }
    // A clone's inputs decide its type again, as live's narrowing does (a reference video's).
    const cloned = cloneDraft(catalog, firstType, clone, initialSource, props.cloneKept);
    return withFrames(catalog, cloned, medium, cloned) ?? cloned;
  });
  // On a delivered output the draft is live's Tweak (#678): one image into one.
  const tweak = isTweak(stored);
  const draft = asTweak(stored);
  // The frames live in the draft, so choosing one keeps the prompt (G1 decision 21); a chosen
  // image's picture and name come with it, and a job's output is read for its picture.
  const source = draft.source;
  const [picked, setPicked] = useState<Record<FrameSlot, Picked | null>>({
    start: null,
    end: null,
  });
  // The library's dialog, with live's words for the tool that opened it, and the slot it fills;
  // the products' popover and the slot it fills (live's one `activeFrame`).
  const [picking, setPicking] = useState<{ dialog: PickingDialog; slot: FrameSlot } | null>(null);
  const [productsFor, setProductsFor] = useState<ProductsTarget | null>(null);
  // A frame's library file the composer has not seen (a link's: the library menu's Edit and
  // Animate; a clone's) is read for its picture (issue 828, item 23).
  const view = useFrameView(teamId, brandId, source, picked.start);
  const endView = useFrameView(teamId, brandId, draft.endFrame, picked.end);
  const types = typesFor(catalog, source !== null || draft.endFrame !== null, medium);
  const [collapsed, setCollapsed] = useState(false);
  const { outputs } = draft;
  // A tool's "auto" keeps its source's shape, which the page does not know, as with a frame.
  const aspectRatio = draft.aspectRatio === "auto" ? null : draft.aspectRatio;
  useEffect(() => {
    onShapeChange({ outputs, aspectRatio });
  }, [outputs, aspectRatio, onShapeChange]);
  // The server fingerprints the whole start body under its key, `expected_quote_max` included.
  const idempotency = useIdempotencyKey();
  const start = useStartJob({ mutation: { onSuccess: onStarted } });
  // The prompt template last applied, with the prompt it put in the draft (G12).
  const [applied, setApplied] = useState<{ id: string; prompt: string } | null>(null);

  // The draft's type is the catalog's: live's narrowing set it (`withFrames`), reference to video
  // included, which no type choice offers.
  const type = catalog.job_types.find((each) => each.id === draft.type) ?? firstType;
  // An Actions tool on the composer (Video Edit, #209): its form, not the inputs, picks the type.
  const tool = isToolType(type.id);
  // A chosen Action narrows the models to those that take it (live's `filterConfigsByAction`).
  const [actionId, setActionId] = useState<string | null>(() => {
    if (linkedTool !== null && linkedToolModels.length > 0) return linkedTool.id;
    // A clone of a tool with a form of its own (Motion Control) opens that form again.
    return clone === undefined ? null : (ACTION_TOOLS.find((each) => each.form === clone.type)?.id ?? null);
  });
  const action = actionOf(actionId);
  // The job a form tool starts from: the clone, while its form is the one open.
  const formClone = clone !== undefined && action?.form === clone.type ? clone : undefined;
  // Live's Tweak form lists the ad-copy rows (its settings' `isAdvanced`); a catalog with none
  // keeps every row, so the form always has a model.
  const offeredModels = modelsTaking(action, modelsFor(catalog, type));
  const tweakModels = offeredModels.filter((each) => "ad_copy" in each && each.ad_copy);
  const models = tweak && tweakModels.length > 0 ? tweakModels : offeredModels;
  const model = models.find((each) => each.id === draft.model) ?? defaultModel(models);
  // Live's Tweak starts its ratio at the image's own and sends it; a pick replaces it.
  const tweaked = useGetJob(teamId, source?.kind === "job_output" ? source.job_id : "", {
    query: { enabled: tweak && source?.kind === "job_output" },
  }).data;
  const imageRatio =
    tweak && source?.kind === "job_output"
      ? tweakRatio(tweaked, source.output_index, model.aspect_ratios.values)
      : null;
  const video = model.medium === "video" ? model : undefined;
  // Live's Multi-Shot caps for this type; with the switch on, an end frame goes only where the
  // caps take one (live drops it from the request, and greys its slot).
  const multiShot = multiShotOf(model, type.id);
  // Live's narrowing over the model's modes: which video inputs show, and which the inputs
  // filled so far leave open (`video/modes.ts`).
  const offeredVideo = offeredVideoTypes(catalog);
  const filled = {
    start: source !== null,
    end: draft.endFrame !== null,
    middle: draft.middleFrames.length,
    references: draft.references.length,
    videos: draft.videos.length,
    audios: draft.audios.length,
    elements: draft.elements.length,
    shots: draft.multiShot,
  };
  const state = (input: VideoInput) =>
    tool ? toolInputState(model, type.id, input) : inputState(model, filled, offeredVideo, input);
  const endState = state("end");
  // An end frame goes only where the mode left takes one: not when greyed, nor when kept out of
  // sight on a model that takes none (live's form keeps it), nor past Multi-Shot's caps.
  const endFrameBlocked =
    endState !== "enabled" || (draft.multiShot && multiShot?.end_frame !== true);
  // Live's elements: the caps of the type's block, else of the first mode that takes any (#229).
  const elementCaps =
    elementCapsOf(model, type.id) ??
    VIDEO_TYPES.map((each) => elementCapsOf(model, each)).find((caps) => caps !== null) ??
    null;
  // The source's sound only where the tool's block can keep it.
  const typeBlock = blockOf(model, type.id);
  const keepsSourceAudio = typeBlock?.keep_source_audio === true;
  // A model that makes the video at its source's resolution (Veo's extensions) takes none.
  const followsSourceResolution = typeBlock?.follows_source_resolution === true;
  // The footer's ratio, length and resolution: live's options for the modes left (issue 366).
  // The draft keeps its picks; what shows and goes is the pick while offered, else the start.
  const options = video === undefined ? null : footerOptions(video, filled, offeredVideo, type.id);
  const footer =
    options === null
      ? null
      : {
          options,
          // A frame keeps its shape: "auto" stays on offer with one (G1 decision 21).
          ratio: chosen(
            { ...options.ratio, auto: options.ratio.auto || filled.start || filled.end },
            draft.aspectRatio,
          ),
          duration: chosen(options.duration, draft.durationSeconds),
          resolution: chosen(options.resolution, draft.resolution),
        };
  // What the request sends: no end frame where blocked, no elements where the type takes none,
  // and the footer's values.
  const sent = {
    ...draft,
    endFrame: endFrameBlocked ? null : draft.endFrame,
    middleFrames: (typeBlock?.middle_frames?.max ?? 0) > 0 ? draft.middleFrames : [],
    elements: sentElements(draft.elements, elementCapsOf(model, type.id)),
    ...(footer === null
      ? {}
      : {
          aspectRatio: footer.ratio,
          durationSeconds: footer.duration,
          resolution: footer.resolution,
        }),
    // A tool's ratio only where the model takes one.
    ...(tool && typeBlock?.takes_aspect_ratio === false ? { aspectRatio: null } : {}),
    ...(followsSourceResolution ? { resolution: null } : {}),
    keepSourceAudio: keepsSourceAudio && draft.keepSourceAudio,
    // A tweak's model is one of its rows, whatever the draft started on, and its ratio the
    // image's own until one is picked.
    ...(tweak ? { model: model.id, aspectRatio: draft.aspectRatio ?? imageRatio } : {}),
  };
  // Live's advanced settings this type takes, and what of them the request sends.
  const advancedCaps = advancedCapsOf(model, type.id);
  const advanced = advancedOf(draft.advanced, advancedCaps);
  const parsed = jobRequestOf(sent, advanced);
  // Priced before the prompt is written, as live is: the quote's body leaves it out.
  const quoted = quoteRequestOf(sent, advanced);
  const body = quoted === undefined ? undefined : JSON.stringify(quoted);
  const [settled] = useDebouncedValue(body, QUOTE_DEBOUNCE_MS);
  const quote = useQuery({
    queryKey: ["quote", teamId, brandId, settled],
    queryFn: ({ signal }) => quoteJob(teamId, brandId, JSON.parse(settled ?? "{}"), { signal }),
    enabled: settled !== undefined,
    retry: false,
    staleTime: 60_000,
  });
  // A price is shown only for exactly what the start would send, its prompt aside.
  // Live's video estimate refuses an empty prompt (issue 346): the bolt alone until it has one, or
  // under Multi-Shot until every shot has one. An image is priced before its prompt, as live's.
  const written =
    medium !== "video" ||
    (draft.multiShot
      ? draft.shots.every((shot) => shot.prompt.trim() !== "")
      : draft.prompt.trim() !== "");
  const priced = written && settled === body ? quote.data : undefined;

  const set = (patch: Partial<GenerateDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
    start.reset();
  };
  // Live's Start with an end frame and no start frame: it stays enabled, and says why it starts
  // nothing (live's `useCreateVideoSubmit`).
  const startMissing = typeBlock?.start_frame === "required" && source === null;
  // A tool's Start without its video, the same way, with live's words for the tool.
  const videoMissing = tool && draft.sourceVideo === null;
  // Said for the draft as it was then: any change of the draft clears it.
  const [saidFor, setSaidFor] = useState<GenerateDraft | null>(null);
  const startJob = () => {
    if (startMissing || videoMissing) {
      setSaidFor(draft);
      return;
    }
    if (!parsed.success || priced === undefined || refusal !== null) return;
    // An image started from a template names it while its prompt is still the template's (G12),
    // so the lightbox's ⓘ can say so (G1 decision 30); an edited prompt names none.
    const type = parsed.data.type;
    const fromTemplate =
      applied !== null && applied.prompt === draft.prompt && (type === "image.simple" || type === "image.tweak");
    const data = {
      ...parsed.data,
      expected_quote_max: priced.credits_max,
      ...(fromTemplate ? { prompt_template_id: applied.id } : {}),
    };
    const key = idempotency.keyFor(data);
    start.mutate(
      { teamId, brandId, data, headers: { "Idempotency-Key": key } },
      {
        onError: (error) => {
          idempotency.failed(key, data, error);
          // The price moved: read it again; the next start is a new body and a new key.
          if (isApiError(error, "QUOTE_CHANGED")) void quote.refetch();
        },
      },
    );
  };

  const count = `${draft.outputs} ${draft.outputs === 1 ? "output" : "outputs"}`;
  // A tool's job has no list word (live's), so the summary names the tool.
  const summary = `${tool && action !== null ? action.title : typeLabel(type.id)}, ${count}`;

  // Choosing or removing a frame moves the draft to the paired type (G1 decision 21).
  const canSource = typesFor(catalog, true, medium).length > 0;
  const chooseFrame = (slot: FrameSlot, next: Picked | null) => {
    const assetId = next?.via === "product" ? next.choice.assetId : next?.image.assetId;
    const ref: SourceRef | null = assetId === undefined ? null : { kind: "asset", asset_id: assetId };
    // A product's photo names its product, for the Library's Product filter (spec image-products).
    const product = next?.via === "product" ? next.choice.productId : null;
    const frames = (current: GenerateDraft): Frames =>
      slot === "start"
        ? { source: ref, sourceProduct: product, endFrame: current.endFrame, references: current.references }
        : {
            source: current.source,
            sourceProduct: current.sourceProduct,
            endFrame: ref,
            references: current.references,
          };
    if (withFrames(catalog, draft, medium, frames(draft)) === undefined) return;
    // From the draft as it is then: an upload ends after the prompt may have changed.
    setDraft((current) => withFrames(catalog, current, medium, frames(current)) ?? current);
    start.reset();
    setPicked((current) => ({ ...current, [slot]: next }));
  };
  const removeSource = () => chooseFrame("start", null);
  // A tweak's references from the library (#233), with their pictures and names, up to what the
  // model's cap leaves beside the source (live's slot budget).
  const [referenceViews, setReferenceViews] = useState<Record<string, PickedImage>>({});
  const cap = referenceCap(model);
  // An image's references share the cap with its product image; a video's are its reference to
  // video block's (live's `max_reference_images`), beside its frames.
  const roomFor = (current: GenerateDraft) =>
    medium === "video"
      ? tool
        ? toolReferencesMax(model, type.id)
        : referenceImagesMax(model, offeredVideo)
      : isTweak(current)
        ? 0
        : referencesLeft(cap, current.source);
  const referencesRoom = roomFor(draft);
  const chooseReferences = (images: PickedImage[]) => {
    const refs: SourceRef[] = images.map((image) => ({ kind: "asset", asset_id: image.assetId }));
    const frames = (current: GenerateDraft): Frames => ({
      source: current.source,
      sourceProduct: current.sourceProduct,
      endFrame: current.endFrame,
      references: refs,
    });
    if (withFrames(catalog, draft, medium, frames(draft)) === undefined) return;
    setDraft((current) => withFrames(catalog, current, medium, frames(current)) ?? current);
    start.reset();
    setReferenceViews((current) => ({
      ...current,
      ...Object.fromEntries(images.map((image) => [image.assetId, image])),
    }));
  };
  // Live's "Apply Prompt" (G12): the template's prompt, and its pictures as the references, those
  // past the room dropped from the end without a word (live's slot trim); the source stays. The
  // template is remembered with its prompt, for the start to name while the prompt is unedited.
  const applyTemplate = (prompt: string, images: PickedImage[], templateId: string) => {
    chooseReferences(images.slice(0, referencesRoom));
    set({ prompt });
    setApplied({ id: templateId, prompt });
  };
  // A dropped or pasted upload joins the references while the cap leaves room; several in a row
  // each see the one before, so the draft is read as it is then.
  const addReference = (image: PickedImage) => {
    setReferenceViews((current) => ({ ...current, [image.assetId]: image }));
    setDraft((current) => {
      if (current.references.length >= roomFor(current)) return current;
      const frames: Frames = {
        source: current.source,
        sourceProduct: current.sourceProduct,
        endFrame: current.endFrame,
        references: [...current.references, { kind: "asset", asset_id: image.assetId }],
      };
      return withFrames(catalog, current, medium, frames) ?? current;
    });
    start.reset();
  };
  // The draft with these elements, its type narrowed again (an element can make the type).
  const withElements = (elements: ElementDraft[]): GenerateDraft => {
    const next = { ...draft, elements };
    return withFrames(catalog, next, medium, next) ?? next;
  };
  // A reference video's videos and audios (#260): up to the most any of the model's modes takes,
  // each from its slot's library dialog or upload.
  // A tool's are its block's alone (Kling 4.0 Edit's, beside its source), for the picker and for
  // another model's reconciliation alike.
  const videosMaxFor = (chosen: CatalogModel) =>
    tool ? toolVideosMax(chosen, type.id) : referenceMax(chosen, offeredVideo, "reference_videos");
  const videosRoom = videosMaxFor(model);
  const audiosRoom = referenceMax(model, offeredVideo, "reference_audios");
  const setMedia = (field: "videos" | "audios", items: PickedImage[]) =>
    setDraft((current) => {
      const next = { ...current, [field]: items };
      return withFrames(catalog, next, medium, next) ?? next;
    });
  const addMedia = (field: "videos" | "audios", item: PickedImage) =>
    setDraft((current) => {
      const room = field === "videos" ? videosRoom : audiosRoom;
      if (current[field].length >= room) return current;
      const next = { ...current, [field]: [...current[field], item] };
      return withFrames(catalog, next, medium, next) ?? next;
    });
  const videoUpload = useFrameUpload(teamId, brandId, (video) => addMedia("videos", video), "video");
  const audioUpload = useFrameUpload(teamId, brandId, (audio) => addMedia("audios", audio), "audio");
  // A video's references from its slot's "Upload" (#232).
  const referenceUpload = useFrameUpload(teamId, brandId, (image) => addReference(image));
  // And from its "Generate" (#985): live's Generate image dialog, its saved image added.
  const referenceGenerate = useGenerateImage({
    teamId,
    brandId,
    onSaved: (asset) => addReference(libraryImage(asset)),
  });
  // Kling 4.0's keyframes between the frames (ours, spec web/keyframes-slot): live's Image refs
  // tile and its menu, up to the most any of the model's modes takes, in the order added.
  const keyframesRoom = middleFramesMax(model, offeredVideo);
  const setKeyframes = (items: PickedImage[]) => {
    setDraft((current) => {
      const next = { ...current, middleFrames: items };
      return withFrames(catalog, next, medium, next) ?? next;
    });
    start.reset();
  };
  const addKeyframe = (image: PickedImage) => {
    setDraft((current) => {
      const held = current.middleFrames;
      if (held.length >= keyframesRoom || held.some((each) => each.assetId === image.assetId)) {
        return current;
      }
      const next = { ...current, middleFrames: [...held, image] };
      return withFrames(catalog, next, medium, next) ?? next;
    });
    start.reset();
  };
  const keyframeUpload = useFrameUpload(teamId, brandId, addKeyframe);
  const keyframeGenerate = useGenerateImage({
    teamId,
    brandId,
    onSaved: (asset) => addKeyframe(libraryImage(asset)),
  });
  // Live's `@Image1` tags, where the type's block takes image tags, and its total cap.
  const block = model.medium === "video" ? model.modes[type.id] : undefined;
  const imageTags = block?.tags?.includes("image") === true;
  const videoTags = block?.tags?.includes("video") === true;
  const audioTags = block?.tags?.includes("audio") === true;
  const elementTags = block?.tags?.includes("element") === true;
  const maxTotal = block?.max_total_references ?? null;
  // Kling 4.0's reference mode counts its frames and keyframes among its images (FAL's 10), as
  // the start refuses them (ours).
  const withFramesMax = block?.max_images_with_frames ?? null;
  const pictures =
    draft.references.length +
    (sent.source === null ? 0 : 1) +
    (sent.endFrame === null ? 0 : 1) +
    sent.middleFrames.length;
  // Images, videos, audios and elements count alike towards the total (live's
  // `getTotalReferenceCount`).
  const refusal =
    maxTotal !== null &&
    draft.references.length + draft.videos.length + draft.audios.length + draft.elements.length >
      maxTotal
      ? `You can use at most ${maxTotal} references in total`
      : withFramesMax !== null && pictures > withFramesMax
        ? `You can use at most ${withFramesMax} images, the frames and keyframes among them`
        : null;
  const removeReference = (assetId: string) =>
    chooseReferences(
      draft.references.flatMap((ref) =>
        ref.kind === "asset" && ref.asset_id !== assetId
          ? [referenceViews[ref.asset_id] ?? pickedOfRef(ref.asset_id)]
          : [],
      ),
    );
  // A reference, video or audio the composer has not seen (a clone's) is read for its picture and
  // its name (issue 828, item 23).
  const unknown = (item: PickedImage | null) => (item !== null && item.url === null && item.original == null ? [item.assetId] : []);
  const unseen = useUnseenFiles(teamId, brandId, [
    ...draft.references.flatMap((ref) => (ref.kind === "asset" && referenceViews[ref.asset_id] === undefined ? [ref.asset_id] : [])),
    ...[...draft.middleFrames, ...draft.videos, ...draft.audios].flatMap(unknown),
    ...draft.elements.flatMap((element) => [element.frontal, ...element.images, element.video].flatMap(unknown)),
  ]);
  const references = draft.references.flatMap((ref) => {
    if (ref.kind !== "asset") return [];
    const read = unseen[ref.asset_id];
    const image = referenceViews[ref.asset_id] ?? (read === undefined ? undefined : { url: pictureOf(read), name: read.name });
    return [{ assetId: ref.asset_id, url: image?.url ?? null, name: image?.name ?? "A reference" }];
  });
  // A tile as the composer shows it: a clone's file read for its still and its name.
  const shown = (item: PickedImage, kind: "image" | "video" | "audio"): PickedImage => {
    const read = item.url === null ? unseen[item.assetId] : undefined;
    if (read === undefined) return item;
    const still = kind === "image" ? pictureOf(read) : kind === "video" ? (read.urls.poster ?? read.urls.thumb ?? null) : null;
    return { ...item, url: still, original: read.urls.original, name: read.name };
  };
  const shownKeyframes = draft.middleFrames.map((item) => shown(item, "image"));
  const shownMedia = (field: "videos" | "audios"): PickedImage[] =>
    draft[field].map((item) => shown(item, field === "videos" ? "video" : "audio"));
  const shownElements: ElementDraft[] = draft.elements.map((element) => ({
    frontal: element.frontal === null ? null : shown(element.frontal, "image"),
    images: element.images.map((image) => shown(image, "image")),
    video: element.video === null ? null : shown(element.video, "video"),
    voice: element.voice,
  }));
  const removeEndFrame = () => chooseFrame("end", null);
  // Live's swap arrow: the start frame and the end frame change places, and the keyframes between
  // them reverse, so the video runs back through them.
  const swapFrames = () => {
    setDraft((current) => ({
      ...current,
      source: current.endFrame,
      endFrame: current.source,
      middleFrames: [...current.middleFrames].reverse(),
    }));
    start.reset();
    setPicked(({ start: first, end: last }) => ({ start: last, end: first }));
  };
  // A model keeps the frames it takes; a dropped end frame that was the only one turns the draft
  // back to text.
  // A clone whose model is gone, or whose library files are, says so until the draft's references
  // are read again.
  const [notice, setNotice] = useState<string | null>(() => {
    if (clone === undefined) return null;
    const left = props.cloneLeftOut ?? 0;
    const files =
      left === 0 ? null : `${countOf(left, "file")} this generation used ${left === 1 ? "is" : "are"} no longer in the library.`;
    // A form tool's clone (Motion Control) fills its form, which falls back to its default model
    // itself; the composer under it starts empty, so it has no model to speak of.
    const model = formClone === undefined ? cloneModelNotice(catalog, clonedOn, clone) : null;
    return [model, files].filter((each) => each !== null).join(" ") || null;
  });
  const chooseModel = (chosen: CatalogModel) => {
    const images = trimmedFor(chosen);
    const next = {
      ...draftFor(type, chosen, draft.prompt, images.source, draft.endFrame),
      // The product goes with its photo while the model keeps it (spec image-products).
      sourceProduct: images.source === draft.source ? draft.sourceProduct : null,
      ...shotsFor(draft, chosen, type.id),
      advanced: draft.advanced,
      references: images.references,
      sourceVideo: draft.sourceVideo,
      keepSourceAudio: draft.keepSourceAudio,
      elements: draft.elements,
      videos: "videos" in images ? images.videos : draft.videos,
      audios: "audios" in images ? images.audios : draft.audios,
      middleFrames: "middleFrames" in images ? images.middleFrames : draft.middleFrames,
    };
    set(withFrames(catalog, next, medium, next) ?? next);
  };
  // Live trims the images to a new model's caps: an image's to its slot budget, without a word;
  // a video's references to the most any of its modes takes, saying so (`useFormReconciliation`).
  const trimmedFor = (chosen: CatalogModel) => {
    if (medium === "image") {
      return trimImages({ source, references: draft.references }, referenceCap(chosen));
    }
    const most = tool ? toolReferencesMax(chosen, type.id) : referenceImagesMax(chosen, offeredVideo);
    // Each medium in turn, in live's order, each with its own words.
    const said: string[] = [];
    const trim = <T,>(items: T[], cap: number, what: string): T[] => {
      if (items.length <= cap) return items;
      said.push(`${what} references were trimmed to ${cap} (model limit).`);
      return items.slice(0, cap);
    };
    const references = trim(draft.references, most, "Image");
    const videos = trim(draft.videos, videosMaxFor(chosen), "Video");
    const audios = trim(draft.audios, referenceMax(chosen, offeredVideo, "reference_audios"), "Audio");
    // The keyframes last, in the references' words (ours); a tool keeps them out of sight.
    const keyframesMost = tool ? draft.middleFrames.length : middleFramesMax(chosen, offeredVideo);
    const middleFrames = draft.middleFrames.slice(0, keyframesMost);
    if (middleFrames.length < draft.middleFrames.length) {
      said.push(`Keyframes were trimmed to ${keyframesMost} (model limit).`);
    }
    setNotice(said.length === 0 ? null : said.join(" "));
    return { source, references, videos, audios, middleFrames };
  };
  // The references within a cap, saying so when some go (live's reconciliation toast).
  const trimmedTo = (most: number): SourceRef[] => {
    if (draft.references.length <= most) {
      setNotice(null);
      return draft.references;
    }
    setNotice(`Image references were trimmed to ${most} (model limit).`);
    return draft.references.slice(0, most);
  };
  // The draft on another type and model, keeping what live's form keeps between tools: the
  // prompt, the frames, the references within the new room, the elements, the settings, the
  // source video and its sound, and a length the model offers too (capture: 7 s stayed).
  const movedTo = (to: CatalogJobType, chosen: CatalogModel): GenerateDraft => {
    const base = draftFor(to, chosen, draft.prompt, draft.source, draft.endFrame);
    const lengths = chosen.medium === "video" ? chosen.durations?.values : undefined;
    const most = isToolType(to.id)
      ? toolReferencesMax(chosen, to.id)
      : referenceImagesMax(chosen, offeredVideo);
    return {
      ...base,
      durationSeconds:
        draft.durationSeconds !== null && lengths?.includes(draft.durationSeconds) === true
          ? draft.durationSeconds
          : base.durationSeconds,
      references: trimmedTo(most),
      // The source stays, and its product with it (spec image-products).
      sourceProduct: draft.sourceProduct,
      // The keyframes stay as the elements do; a type without them sends none (`sent`).
      middleFrames: draft.middleFrames,
      elements: draft.elements,
      advanced: draft.advanced,
      sourceVideo: draft.sourceVideo,
      keepSourceAudio: draft.keepSourceAudio,
    };
  };
  // Live keeps the model when the Action takes it; else ours moves to the Action's default. A
  // tool on the composer (Video Edit) makes its own type on its rows; leaving it goes back to
  // the default composer, where the inputs narrow the type again.
  const chooseAction = (next: ActionTool | null) => {
    const toolType = catalog.job_types.find((each) => each.id === next?.job?.type);
    const toolModels = toolType === undefined ? [] : modelsFor(catalog, toolType);
    if (next?.job !== undefined && toolModels.length === 0) return;
    setActionId(next?.id ?? null);
    if (toolType !== undefined) {
      set(movedTo(toolType, defaultModel(toolModels)));
      return;
    }
    if (tool) {
      const home = typesFor(catalog, false, medium)[0];
      if (home === undefined) return;
      const moved = movedTo(home, defaultModel(modelsTaking(next, modelsFor(catalog, home))));
      set(withFrames(catalog, moved, medium, moved) ?? moved);
      return;
    }
    const offered = modelsTaking(next, modelsFor(catalog, type));
    if (!offered.some((each) => each.id === model.id)) chooseModel(defaultModel(offered));
  };
  // Live's "Video ref" on a tool (#209): the one video it works on, from the library or an
  // upload, with its poster and name.
  const [sourceVideoView, setSourceVideoView] = useState<PickedImage | null>(null);
  const chooseSourceVideo = (video: PickedImage | null) => {
    set({ sourceVideo: video === null ? null : { kind: "asset", asset_id: video.assetId } });
    setSourceVideoView(video);
  };
  const sourceVideoUpload = useFrameUpload(teamId, brandId, chooseSourceVideo, "video");
  const [videoPicking, setVideoPicking] = useState(false);
  // The tool's library video the composer has not seen (a link's: a video's Edit, S1c; a clone's),
  // read for its name and poster.
  const unseenVideo =
    draft.sourceVideo?.kind === "asset" && sourceVideoView?.assetId !== draft.sourceVideo.asset_id
      ? draft.sourceVideo.asset_id
      : null;
  const linkedVideo = useGetAsset(teamId, brandId, unseenVideo ?? "", {
    query: { enabled: unseenVideo !== null },
  }).data;
  // An earlier job's output a clone's tool works on (a Video Edit of a Studio video, issue 828
  // item 23), read from its job for its poster.
  const earlierRef = draft.sourceVideo?.kind === "job_output" ? draft.sourceVideo : null;
  const earlierVideo = useGetJob(teamId, earlierRef?.job_id ?? "", {
    query: { enabled: earlierRef !== null },
  }).data?.outputs.find((each) => each.index === earlierRef?.output_index);
  const sourceVideos =
    draft.sourceVideo?.kind === "asset"
      ? [
          sourceVideoView?.assetId === draft.sourceVideo.asset_id
            ? sourceVideoView
            : linkedVideo?.id === draft.sourceVideo.asset_id
              ? // A video's still, never its file as a picture (f2b on #672); without one, the
                // slot shows the video's first frame from its file (`original`), as a picked one.
                {
                  assetId: linkedVideo.id,
                  url: linkedVideo.urls.poster ?? linkedVideo.urls.thumb ?? null,
                  original: linkedVideo.urls.original,
                  name: linkedVideo.name,
                }
              : { assetId: draft.sourceVideo.asset_id, url: null, name: "The video" },
        ]
      : earlierRef === null
        ? []
        : [
            {
              // The slot keys its item by this; an output is no library file.
              assetId: `${earlierRef.job_id}:${earlierRef.output_index}`,
              url: earlierVideo?.poster_url ?? earlierVideo?.thumb_url ?? null,
              original: earlierVideo?.url ?? null,
              name: `Video ${earlierRef.output_index + 1} of an earlier job`,
            },
          ];
  // Each frame's "Upload" (live's capture 22): the library's one upload, then the frame.
  const frameUpload = useFrameUpload(teamId, brandId, (image) =>
    chooseFrame("start", { via: "library", image }),
  );
  const endUpload = useFrameUpload(teamId, brandId, (image) =>
    chooseFrame("end", { via: "library", image }),
  );
  // Each frame's "Generate" (#985): live's "Generate frame" dialog, its saved image the frame.
  // Each frame keeps its own image made and not saved, as live keys it per frame.
  const frameGenerate = useGenerateImage({
    teamId,
    brandId,
    title: FRAME_GENERATE,
    onSaved: (asset) => chooseFrame("start", { via: "library", image: libraryImage(asset) }),
  });
  const endGenerate = useGenerateImage({
    teamId,
    brandId,
    title: FRAME_GENERATE,
    onSaved: (asset) => chooseFrame("end", { via: "library", image: libraryImage(asset) }),
  });
  const product =
    source?.kind === "asset" && picked.start?.via === "product" ? picked.start.choice : null;
  const from =
    source?.kind === "job_output" ? (
      <Text size="sm">
        From image {source.output_index + 1} of{" "}
        <Anchor
          renderRoot={(linkProps) => (
            <Link
              {...linkProps}
              to="/teams/$teamId/jobs/$jobId"
              params={{ teamId, jobId: source.job_id }}
            />
          )}
        >
          an earlier job
        </Anchor>
        .
      </Text>
    ) : null;
  // Live's composer shows a product as its active tool, and a reference under the prompt.
  const reference = product === null ? view : null;
  // One popover, anchored at the slot it fills (`productsSlot`).
  const productsSlot = productsFor ?? "start";
  const productsPicked =
    productsSlot === "references" || productsSlot === "keyframes" ? null : picked[productsSlot];
  const products = (anchor: Parameters<typeof ProductPicker>[0]["anchor"]): ReactNode => (
    <ProductPicker
      teamId={teamId}
      brandId={brandId}
      chosen={
        productsSlot === "start"
          ? product
          : draft.endFrame?.kind === "asset" && productsPicked?.via === "product"
            ? productsPicked.choice
            : null
      }
      opened={productsFor !== null}
      onClose={() => setProductsFor(null)}
      onConfirm={(choice) =>
        productsSlot === "references"
          ? addReference({ assetId: choice.assetId, url: choice.url, name: choice.name })
          : productsSlot === "keyframes"
            ? addKeyframe({ assetId: choice.assetId, url: choice.url, name: choice.name })
            : chooseFrame(productsSlot, { via: "product", choice })
      }
      anchor={anchor}
    />
  );
  // The image composer's Products tool toggles the popover; a frame's "From product" opens it.
  const toggleProducts = () => setProductsFor((open) => (open === null ? "start" : null));
  const openProducts = (slot: ProductsTarget) => setProductsFor(slot);
  const openLibrary = (dialog: PickingDialog, slot: FrameSlot = "start") =>
    setPicking({ dialog, slot });
  const pickingRef = picking === null ? null : picking.slot === "start" ? source : draft.endFrame;
  const pickingPicked = picking === null ? null : picked[picking.slot];
  // Live's "Video References" and "Audio References" (#260).
  const [mediaPicking, setMediaPicking] = useState<"videos" | "audios" | null>(null);
  const mediaDialog =
    mediaPicking === null ? null : (
      <ReferencesDialog
        teamId={teamId}
        brandId={brandId}
        kind={mediaPicking === "videos" ? "video" : "audio"}
        title={mediaPicking === "videos" ? "Video References" : "Audio References"}
        // Live's audio list says no count.
        {...(mediaPicking === "videos" ? { subtitle: `Select up to ${videosRoom}` } : {})}
        max={mediaPicking === "videos" ? videosRoom : audiosRoom}
        chosen={draft[mediaPicking].map((each) => each.assetId)}
        onClose={() => setMediaPicking(null)}
        onConfirm={(items) => setMedia(mediaPicking, items)}
      />
    );
  // Live's "Image References": several, up to what the cap leaves (#233).
  const [referencesOpen, setReferencesOpen] = useState(false);
  const referencesDialog = referencesOpen ? (
    <ReferencesDialog
      teamId={teamId}
      brandId={brandId}
      title={medium === "video" ? "Select Image References" : "Image References"}
      // Live's words, in the singular for a room of one (live's "1 references").
      subtitle={
        medium === "video"
          ? `Choose images to reference · Select up to ${referencesRoom}`
          : selectUpTo(referencesRoom, "reference")
      }
      max={referencesRoom}
      chosen={references.map((each) => each.assetId)}
      onClose={() => setReferencesOpen(false)}
      onConfirm={chooseReferences}
    />
  ) : null;
  // The keyframes' "From library": several, in the order picked, up to the room (ours).
  const [keyframesOpen, setKeyframesOpen] = useState(false);
  const keyframesDialog = keyframesOpen ? (
    <ReferencesDialog
      teamId={teamId}
      brandId={brandId}
      title="Select Keyframes"
      subtitle={`Choose images for the keyframes · Select up to ${keyframesRoom}`}
      max={keyframesRoom}
      chosen={draft.middleFrames.map((each) => each.assetId)}
      // A keyframe no page read so far lists (a clone's older file) keeps its place on Confirm.
      held={shownKeyframes}
      onClose={() => setKeyframesOpen(false)}
      onConfirm={setKeyframes}
    />
  ) : null;
  // Live's "Video References" for a tool's one video (`video-refs-modal`).
  const videoDialog = videoPicking ? (
    <ReferencesDialog
      teamId={teamId}
      brandId={brandId}
      kind="video"
      title="Video References"
      subtitle="Select up to 1"
      max={1}
      chosen={draft.sourceVideo?.kind === "asset" ? [draft.sourceVideo.asset_id] : []}
      onClose={() => setVideoPicking(false)}
      onConfirm={(videos) => chooseSourceVideo(videos[0] ?? null)}
    />
  ) : null;
  const generating =
    frameGenerate.dialog ?? endGenerate.dialog ?? referenceGenerate.dialog ?? keyframeGenerate.dialog;
  const dialog =
    picking === null ? (
      (videoDialog ?? referencesDialog ?? keyframesDialog ?? mediaDialog ?? generating)
    ) : (
      <ReferencesDialog
        teamId={teamId}
        brandId={brandId}
        title={picking.dialog.title}
        subtitle={picking.dialog.subtitle}
        max={1}
        chosen={
          pickingRef?.kind === "asset" && pickingPicked?.via === "library"
            ? [pickingRef.asset_id]
            : []
        }
        onClose={() => setPicking(null)}
        onConfirm={(images) => {
          const image = images[0];
          if (image !== undefined) chooseFrame(picking.slot, { via: "library", image });
        }}
      />
    );

  return {
    catalog,
    medium,
    draft,
    shownMedia,
    shownElements,
    formClone,
    // Live's Tweak (#678): the composer shows its form's settings only, its ratio the image's.
    tweak,
    imageRatio,
    set,
    types,
    type,
    models,
    model,
    video,
    parsed,
    quote,
    priced,
    // The quote is running, or the draft changed since it was asked.
    pricing: written && (quote.isFetching || settled !== body),
    quoteFailed: quote.isError && settled === body,
    start,
    startJob,
    summary,
    collapsed,
    setCollapsed,
    source,
    canSource,
    removeSource,
    removeEndFrame,
    swapFrames,
    chooseModel,
    action,
    chooseAction,
    tool,
    sourceVideos,
    openSourceVideo: () => setVideoPicking(true),
    sourceVideoUpload,
    removeSourceVideo: () => chooseSourceVideo(null),
    keepsSourceAudio,
    // The video is made at its source's resolution (Veo's extensions): no choice to send.
    followsSourceResolution,
    // Live's advanced button opens where the type takes a setting, its sound one included.
    advancedOffered: advancedCaps !== null || keepsSourceAudio,
    endFrameOffered: endState !== "hidden",
    endFrameBlocked,
    startState: state("start"),
    referencesState: state("references"),
    // Kling 4.0's keyframes (ours, spec web/keyframes-slot).
    keyframesState: state("middle"),
    keyframesRoom,
    shownKeyframes,
    openKeyframes: () => setKeyframesOpen(true),
    keyframeUpload,
    keyframeGenerate,
    removeKeyframe: (assetId: string) =>
      setKeyframes(draft.middleFrames.filter((each) => each.assetId !== assetId)),
    multiShot,
    // The type's shortest video, which the shots together never last less than (spec
    // web/shot-defaults).
    shortestVideo: shortestOf(model, type.id),
    advancedCaps,
    setAdvanced: (patch: Partial<AdvancedDraft>) =>
      set({ advanced: { ...draft.advanced, ...patch } }),
    // Live shows the switch when any of the model's modes makes multi-shot video.
    multiShotOffered: (["video.text", "video.image"] as const).some(
      (each) => multiShotOf(model, each) !== null,
    ),
    view,
    endView,
    product,
    reference,
    from,
    products,
    productsOpen: productsFor !== null,
    productsSlot,
    toggleProducts,
    openProducts,
    openLibrary,
    dialog,
    frameUpload,
    endUpload,
    frameGenerate,
    endGenerate,
    references,
    referencesRoom,
    // Live's info after a model change trimmed the references, until the next change.
    notice,
    referenceUpload,
    referenceGenerate,
    footer,
    // Live's usage notes for the job the draft starts: its type's block's (#377, G6 decision 15).
    notes: block?.notes ?? [],
    // Live's prompt cap is the mode's where it has its own (Wan's Multi-Input 5000, #260).
    promptMax: block?.prompt_max_chars ?? model.prompt_max_chars,
    imageTags,
    // Live's `@Video1`, on the tool's video and the reference videos, where the block tags videos.
    videoTags,
    audioTags,
    elementTags,
    // The route's team and brand: the voices' list reads them (G2-kling-element-voice-picker).
    teamId,
    brandId,
    videosState: state("videos"),
    audiosState: state("audios"),
    videosRoom,
    audiosRoom,
    setMedia,
    openMedia: (field: "videos" | "audios") => setMediaPicking(field),
    removeMedia: (field: "videos" | "audios", assetId: string) =>
      setMedia(
        field,
        draft[field].filter((each) => each.assetId !== assetId),
      ),
    videoUpload,
    audioUpload,
    // Live's slot tooltips: the mode's media limits (#302) as live's lines, none where it has
    // none (issue 461).
    limits: {
      image: imageLimitLines(block?.media_limits?.image),
      video: videoLimitLines(block?.media_limits?.video),
      audio: audioLimitLines(block?.media_limits?.audio),
    },
    elementCaps,
    elementsState: state("elements"),
    addElement: (element: ElementDraft) =>
      set({ ...withElements([...draft.elements, element]) }),
    replaceElement: (at: number, element: ElementDraft) =>
      set({ ...withElements(draft.elements.map((each, index) => (index === at ? element : each))) }),
    removeElement: (at: number) =>
      set({ ...withElements(draft.elements.filter((_, index) => index !== at)) }),
    // Live's word at the start when the references pass the type's total (live: a toast).
    refusal,
    // Start stays enabled to say what is missing, as live's does.
    startMissing: startMissing || videoMissing,
    // Live's words once Start is pressed without the start frame its mode needs, or without a
    // tool's video.
    startProblem:
      saidFor !== draft
        ? null
        : videoMissing
          ? (action?.job?.needsVideo ?? null)
          : startMissing
            ? "Start frame is required for image to video generation"
            : null,
    openReferences: () => setReferencesOpen(true),
    chooseReferences,
    applyTemplate,
    // A product photo as the source, or none (the image Advanced mode hands its own back, #234).
    chooseProduct: (choice: ProductChoice | null) =>
      chooseFrame("start", choice === null ? null : { via: "product", choice }),
    addReference,
    removeReference,
  };
}

/** A reference whose picture the composer has not seen (a clone's): its id and a plain name. */
function pickedOfRef(assetId: string): PickedImage {
  return { assetId, url: null, name: "A reference" };
}

/** A library image as a slot holds it, as an upload's: its picture, its file and its name. */
function libraryImage(asset: Asset): PickedImage {
  return { assetId: asset.id, url: pictureOf(asset), original: asset.urls.original, name: asset.name };
}

/** The library files of `ids`, read for their pictures and names, by id: one read each, from
 *  the cache where the page holds it. */
function useUnseenFiles(teamId: string, brandId: string, ids: readonly string[]): Record<string, Asset> {
  const reads = useQueries({ queries: ids.map((id) => getGetAssetQueryOptions(teamId, brandId, id)) });
  const files: Record<string, Asset> = {};
  reads.forEach((read, at) => {
    if (read.data !== undefined) files[ids[at]!] = read.data;
  });
  return files;
}

/** The models that take the Action, or all of them without one (or when none does). */
function modelsTaking(action: ActionTool | null, models: CatalogModel[]): CatalogModel[] {
  if (action === null) return models;
  const taking = models.filter((each) => each.medium === "video" && action.takes(each));
  return taking.length > 0 ? taking : models;
}

/**
 * A frame's picture and name: the chosen image's, or a job's output read for its picture, or
 * null for an empty slot.
 */
function useFrameView(
  teamId: string,
  brandId: string,
  ref: SourceRef | null,
  picked: Picked | null,
): SourceView | null {
  const earlier = useGetJob(teamId, ref?.kind === "job_output" ? ref.job_id : "", {
    query: { enabled: ref?.kind === "job_output" },
  }).data;
  // A library file chosen here carries its picture; one the composer has not seen (a link's, a
  // clone's) is read for it, else its tile is a grey square (issue 828, item 23).
  const unseen = ref?.kind === "asset" && picked === null;
  const file = useGetAsset(teamId, brandId, unseen ? ref.asset_id : "", { query: { enabled: unseen } }).data;
  if (ref?.kind === "asset") {
    const image = picked?.via === "product" ? picked.choice : picked?.image;
    const read = unseen ? file : undefined;
    return {
      url: image?.url ?? read?.urls.thumb ?? read?.urls.original ?? null,
      name: image?.name ?? read?.name ?? "The chosen image",
    };
  }
  if (ref?.kind === "job_output") {
    const output = earlier?.outputs.find((each) => each.index === ref.output_index);
    return { url: output?.url ?? null, name: `Image ${ref.output_index + 1} of an earlier job` };
  }
  return null;
}

/**
 * A frame's upload into the brand's library, then the frame. An upload still running when the
 * composer goes is aborted, and the flow deletes its asset.
 */
function useFrameUpload(
  teamId: string,
  brandId: string,
  onUploaded: (image: PickedImage) => void,
  kind: "image" | "video" | "audio" = "image",
): ImageUpload {
  const uploading = useRef<AbortController | null>(null);
  useEffect(() => () => uploading.current?.abort(), []);
  return useImageUpload({
    teamId,
    brandId,
    kind,
    onUploaded: (asset) =>
      onUploaded({
        assetId: asset.id,
        url: kind === "image" ? pictureOf(asset) : (asset.urls.poster ?? asset.urls.thumb ?? null),
        // A video just uploaded has no still yet: the slot shows a frame of its file.
        original: asset.urls.original,
        name: asset.name,
      }),
    onUploading: (controller) => {
      uploading.current = controller;
    },
  });
}

export type Composer = ReturnType<typeof useComposer>;

/** The model's block for a video type, or undefined. */
function blockOf(model: CatalogModel, type: GenerationType) {
  return model.medium === "video" ? model.modes[type] : undefined;
}
